import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { createChartXAxisLabel } from "utils/charts";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetEMSMemberEnergyQuery } from "state/communities/communitiesApiSlice";
import { useGetResourcesByCommunityIdQuery } from "state/resources/resourcesApiSlice";

import EmsChartCard from "./EmsChartCard";

const PALETTE = ["#10b5a0", "#2f82d6", "#f59e0b", "#16a34a", "#7928CA", "#e23b54"];

/* Chart 1 — battery state of charge (%) per battery over the day. */
function PromoterBatSocChart({ moduleType, startDatetime, endDatetime, onDataStatus }) {
  const { t } = useTranslation(["community"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const { data: rawList = [], isLoading } = useGetEMSMemberEnergyQuery(
    { communityId, moduleType, startDatetime, endDatetime, limit: 5000, offset: 0 },
    { skip: !communityId }
  );

  const { data: resources } = useGetResourcesByCommunityIdQuery(
    { communityId, excludeDeleted: true },
    { skip: !communityId }
  );

  const resName = (resId) =>
    resources?.entities?.[resId]?.name || t("unified_ems.promoter_bat_label");

  const hasSoc = useMemo(
    () => rawList.some((r) => Object.keys(r.metadata?.soc_bat || {}).length > 0),
    [rawList]
  );
  const isEmpty = !isLoading && !hasSoc;
  useEffect(() => {
    if (!isLoading && onDataStatus) onDataStatus(!isEmpty);
  }, [isLoading, isEmpty, onDataStatus]);

  const { series, labels } = useMemo(() => {
    if (!hasSoc) return { series: [], labels: [] };
    const byRes = new Map();
    const tsSet = new Set();
    rawList.forEach((r) => {
      if (!r.datetime_timestamp) return;
      tsSet.add(r.datetime_timestamp);
      Object.entries(r.metadata?.soc_bat || {}).forEach(([resId, val]) => {
        if (!byRes.has(resId)) byRes.set(resId, new Map());
        byRes.get(resId).set(r.datetime_timestamp, Number(Number(val).toFixed(1)));
      });
    });
    const sorted = [...tsSet].sort((a, b) => new Date(a) - new Date(b));
    const result = [...byRes.entries()].map(([resId, tsMap], i) => ({
      name: resName(resId),
      data: sorted.map((ts) => (tsMap.has(ts) ? tsMap.get(ts) : null)),
      color: PALETTE[i % PALETTE.length],
    }));
    return { series: result, labels: sorted.map((ts) => createChartXAxisLabel(ts, "MIN15")) };
  }, [rawList, hasSoc, resources]);

  if (isLoading || isEmpty) return null;
  const legend = series.map((s) => ({ label: s.name, color: s.color }));

  return (
    <EmsChartCard
      title={t("unified_ems.promoter_bat_soc_chart_title")}
      sub={t("unified_ems.promoter_bat_soc_chart_sub")}
      legend={legend}
      series={series}
      labels={labels}
      height={420}
      unit="%"
      yTitle="%"
      type="area"
    />
  );
}

PromoterBatSocChart.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
  onDataStatus: undefined,
};
PromoterBatSocChart.propTypes = {
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
  onDataStatus: PropTypes.func,
};

export default PromoterBatSocChart;
